import { ZodError, z } from 'zod';
import { gameVariableSchema } from '@/entities/project/model/project';
import { createId } from '@/shared/lib/id/createId';
/** 项目文档中可扩展的配置对象，细节由对应页签在本地负责维护。 */
const extensibleObjectSchema = z.record(z.string(), z.unknown());
/** 事件绑定到视频时间点的锚点，是事件触发时间的唯一事实。 */
export const eventAnchorSchema = z.object({
    clipId: z.string().nullable(),
    offsetMs: z.number().int(),
    mode: z.enum(['clip-relative', 'sequence-absolute']),
});
/**
 * 画布百分比布局：x/y/width/height 均为画布宽高的百分比。
 * 复用于 descriptionLayout / countdownLayout / optionLayouts / QTE layout，
 * 保证布局与语义分离：业务含义只放在 event 或 branch，布局只管位置和尺寸。
 * height 字段 .default(0) 兼容历史上缺失 height 的旧文档。
 */
export const percentLayoutSchema = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number().default(0),
});
/**
 * 超时规则取值：jump-node 超时自动跳转、none 不跳转。
 * 「不跳转」只冻结播放节奏——超时后暂停视频并保留选项/QTE 展示，玩家仍可继续操作；
 * 它不产生任何结果，也不会写入完成集合。
 */
export const INTERACTION_TIMEOUT_ACTIONS = ['jump-node', 'none'];
/**
 * 超时规则默认值：不跳转，与属性面板默认选中项一致。
 * 限时到点时不替玩家做决定，只暂停视频并保留互动展示，等待玩家继续操作。
 */
export const DEFAULT_INTERACTION_TIMEOUT_ACTION = 'none';
/** 选项结果的变量动作运算符：赋值、增加、减少。 */
export const INTERACTION_VARIABLE_OPERATORS = ['SET', 'INCREASE', 'DECREASE'];
/**
 * 选项结果的变量动作：玩家选中该选项后对某个项目数值执行一次整数运算。
 * 变量名引用项目数值事实源（cg_project.game_variables）；不在这里保存变量定义或初始值。
 */
export const optionVariableEffectSchema = z.object({
    /** 被修改的项目数值名称。 */
    variableName: z.string().min(1).max(30),
    /** 运算方式：赋值 / 增加 / 减少。 */
    operator: z.enum(INTERACTION_VARIABLE_OPERATORS),
    /** 参与运算的整数值。 */
    value: z.number().int(),
});
/** 一个结果可配置多条变量动作，按数组顺序依次应用；空数组表示不修改任何数值。 */
export const optionVariableEffectsSchema = z.array(optionVariableEffectSchema);
/**
 * 分支结果的触发方式：
 * immediate-结果成立即跳转；delay-延迟若干毫秒后跳转；complete-当前节点视频播完再跳转。
 */
export const BRANCH_TRIGGER_MODES = ['immediate', 'delay', 'complete'];
/** 分支结果默认触发方式：把当前节点视频播完再跳转。 */
export const DEFAULT_BRANCH_TRIGGER_MODE = 'complete';
/**
 * 分支结果的触发事件配置：QTE 成败与每个选项各自一份。
 *
 * 「完播」是默认值：结果成立后继续播放当前节点视频，播完再跳转；
 * 「立即」结果成立即跳转；「延迟」在结果成立后等满 delayMs 再跳转。
 */
export const branchTriggerSchema = z.object({
    /** 触发方式：immediate-立即跳转，delay-延迟跳转，complete-播完当前节点再跳。 */
    mode: z.enum(BRANCH_TRIGGER_MODES).default(DEFAULT_BRANCH_TRIGGER_MODE),
    /** 延迟毫秒数；mode 为 immediate 时忽略。 */
    delayMs: z.number().int().min(0).default(0),
});
/** 分支结果默认触发配置：把当前节点视频播完再跳转。 */
export const DEFAULT_BRANCH_TRIGGER = { mode: 'complete', delayMs: 0 };
/** 选项事件的单个选项配置；只保存内容和路由，布局统一放到 optionLayouts[optionId]。 */
export const eventOptionSchema = z.object({
    id: z.string(),
    label: z.string(),
    order: z.number().int(),
    branchId: z.string().nullable(),
    /** 选中该选项后要修改的数值动作列表；空数组表示不修改任何数值（默认）。 */
    variableEffects: optionVariableEffectsSchema.default([]),
    /** 选中该选项后的跳转时机；缺字段按默认「完播」处理。 */
    trigger: branchTriggerSchema.optional(),
});
/** 选项按钮排列方式：横向单排（默认）或纵向单列。 */
export const CHOICE_OPTION_LAYOUT_MODES = ['horizontal', 'vertical'];
/** 选项按钮默认排列方式：横向单排，与既有默认几何口径一致。 */
export const DEFAULT_CHOICE_OPTION_LAYOUT_MODE = 'horizontal';
/** 交互文案对齐方式：左对齐 / 居中 / 右对齐 / 两端对齐。 */
export const TEXT_STYLE_ALIGNMENTS = ['left', 'center', 'right', 'justify'];
/** 默认字体族标识：表示跟随作品默认字体，不指定具体字型。 */
export const DEFAULT_TEXT_STYLE_FONT_FAMILY = 'default';
/**
 * 交互文案默认字号：占画布宽度的百分比。
 * 0.78% 约等于 1920 宽画布下的 15px；与布局字段（x/y/宽/高）用同一套百分比锚点，
 * 因此换画布比例时文字会和选项框一起等比缩放。
 */
export const DEFAULT_TEXT_STYLE_FONT_SIZE_PERCENT = 0.78;
/** 交互文案默认颜色（白色）。 */
export const DEFAULT_TEXT_STYLE_COLOR = '#FFFFFF';
/**
 * 交互文案的字体样式。
 *
 * 文案样式属于事件内容，挂在事件配置上随 events_json 保存，**不进** event_assets_json：
 * 后者只承载选项按钮与各类 QTE 的图片 / 音频地址。样式与 description 这类文案内容同处
 * 事件配置，随 UPDATE_EVENT 一起落库，口径统一。
 *
 * 同一份结构服务选项事件的提示文案、选项文案与 QTE 的引导文案，由各自的配置字段持有。
 * 尺寸类字段一律是「画布宽度百分比」，与选项布局（optionLayouts）保持同一口径：
 * 像素值在非 16:9 画布下含义会漂移，只有百分比能随画布比例稳定缩放。
 * 字段为后续增补，历史上已入库的事件缺失该字段时按各文案位的兜底样式补全。
 */
export const textStyleSchema = z.object({
    /** 字体族标识；'default' 表示跟随作品默认字体。 */
    fontFamily: z.string().catch(DEFAULT_TEXT_STYLE_FONT_FAMILY),
    /** 字号（画布宽度百分比）。 */
    fontSizePercent: z.number().catch(DEFAULT_TEXT_STYLE_FONT_SIZE_PERCENT),
    /** 是否加粗。 */
    bold: z.boolean().catch(false),
    /** 是否下划线。 */
    underline: z.boolean().catch(false),
    /** 是否斜体。 */
    italic: z.boolean().catch(false),
    /** 文字颜色（十六进制色值）。 */
    color: z.string().catch(DEFAULT_TEXT_STYLE_COLOR),
    /** 字间距（画布宽度百分比，可为负）。 */
    letterSpacingPercent: z.number().catch(0),
    /** 行间距：在字号之上额外增加的行间距（画布宽度百分比，可为负）。 */
    lineHeightPercent: z.number().catch(0),
    /** 对齐方式。 */
    align: z.enum(TEXT_STYLE_ALIGNMENTS).catch('center'),
});
/** 返回一份交互文案默认样式：默认字体、0.78% 字号、白色居中，供 schema 兜底与新建事件使用。 */
export function createDefaultTextStyle() {
    return {
        fontFamily: DEFAULT_TEXT_STYLE_FONT_FAMILY,
        fontSizePercent: DEFAULT_TEXT_STYLE_FONT_SIZE_PERCENT,
        bold: false,
        underline: false,
        italic: false,
        color: DEFAULT_TEXT_STYLE_COLOR,
        letterSpacingPercent: 0,
        lineHeightPercent: 0,
        align: 'center',
    };
}
/**
 * 提示文案的兜底样式。
 * 字号 0.94% 对齐画布宽 1920 下的 18px；行间距补足原先 1.3 倍行高多出的部分。
 */
export const DEFAULT_PROMPT_TEXT_STYLE = {
    ...createDefaultTextStyle(),
    fontSizePercent: 0.94,
    lineHeightPercent: 0.28,
};
/**
 * 选项文案的兜底样式。
 * 字号 0.73% 对齐画布宽 1920 下的 14px；同一事件的所有选项共用这一份样式。
 */
export const DEFAULT_OPTION_TEXT_STYLE = {
    ...createDefaultTextStyle(),
    fontSizePercent: 0.73,
    lineHeightPercent: 0.15,
};
/**
 * QTE 引导文案的兜底样式。
 * 字号 0.83% 对齐画布宽 1920 下的 16px。
 */
export const DEFAULT_QTE_DESCRIPTION_TEXT_STYLE = {
    ...createDefaultTextStyle(),
    fontSizePercent: 0.83,
    lineHeightPercent: 0.25,
};
/**
 * 样式编辑的应用范围：仅当前事件 / 当前篇章 / 指定篇章 / 全局。
 *
 * 只作用于 **UI 样式（素材位）**，即 cg_story_node.event_assets_json 里的图片 / 音频地址：
 * 它决定这次改的素材位要覆写到哪些剧情节点的 event_assets_json。
 * 文案样式不走范围——文案属于事件内容（events_json），改哪条事件就只改哪条。
 */
export const TEXT_STYLE_SCOPES = [
    'current-event',
    'current-chapter',
    'specified-chapter',
    'global',
];
/** 样式编辑默认应用范围：仅作用于当前事件。 */
export const DEFAULT_TEXT_STYLE_SCOPE = 'current-event';
/** 选项事件的运行配置。 */
export const choiceEventConfigSchema = z.object({
    variant: z.enum(['image-text', 'text']),
    backgroundAssetId: z.string().nullable(),
    description: z.string(),
    /** 选项按钮排列方式；决定选项数量变化与排列方式切换时的默认几何（存量文档缺字段回退横向）。 */
    optionLayout: z.enum(CHOICE_OPTION_LAYOUT_MODES).catch(DEFAULT_CHOICE_OPTION_LAYOUT_MODE),
    pauseVideo: z.boolean(),
    /** 限时毫秒；null 表示不限时，持续展示选项直到选择；有帧循环时循环播放，否则暂停等待。 */
    timeoutMs: z.number().int().nullable(),
    /**
     * 超时规则：不跳转（默认）或 超时自动跳转。
     * 已下线的历史值 auto-select 统一回落为 none（不跳转），避免静默丢弃玩家的选择权。
     */
    timeoutAction: z.enum(INTERACTION_TIMEOUT_ACTIONS).catch('none'),
    /**
     * 超时跳转的目标剧情节点 ID；仅在「超时自动跳转」下生效。
     * 不经分支与连线路由，避免与剧情树章节保存的 connections 写入互相覆盖。
     */
    timeoutJumpTargetNodeId: z.string().nullable().default(null),
    /** 事件级帧循环区间；null 表示选项出现时不开启帧循环（默认）。 */
    frameLoop: z
        .object({
        startMs: z.number().int(),
        endMs: z.number().int(),
    })
        .nullable(),
    /** 文字描述的独立布局；null 表示从选项位置自动派生（旧数据兼容）。 */
    descriptionLayout: percentLayoutSchema.nullable().default(null),
    /** 倒计时进度条的独立布局；null 表示从选项位置自动派生（旧数据兼容）。 */
    countdownLayout: percentLayoutSchema.nullable().default(null),
    /** 提示文案的字体样式；文案样式属于事件内容，随 events_json 保存。 */
    promptTextStyle: textStyleSchema.default(DEFAULT_PROMPT_TEXT_STYLE),
    /** 选项文案的字体样式；同一事件的所有选项共用这一份。 */
    optionTextStyle: textStyleSchema.default(DEFAULT_OPTION_TEXT_STYLE),
    /** 素材位的应用范围；只作用于 UI 样式（event_assets_json），文案样式不受它影响。 */
    styleScope: z.enum(TEXT_STYLE_SCOPES).catch(DEFAULT_TEXT_STYLE_SCOPE),
    /** 「用于指定篇章」时目标章节 ID；其他范围下为 null。 */
    styleScopeChapterId: z.string().nullable().default(null),
});
/**
 * 滑动 QTE 的可选方向：四个正方向加四个斜向，顺序与属性面板的按钮网格一致。
 * 方向既用于判定玩家滑动是否达标，也用于画布上主视觉图的朝向。
 */
/**
 * 连点 QTE 的两种成功模式。
 *
 * - `count`：按次数成功——在「操作时间」内点满目标次数即成功；超时按事件的超时规则处理。
 * - `energy`：积攒能量——每次点击加能量、按时间持续衰减，攒满目标能量即成功。
 */
export const QTE_RAPID_CLICK_MODES = ['count', 'energy'];
/** 连点 QTE 默认模式：积攒能量（与既有能量三参数口径一致）。 */
export const DEFAULT_QTE_RAPID_CLICK_MODE = 'energy';
/**
 * 长按 QTE 的两种中断行为。
 *
 * - `reset`：松开立即清零，再次按住从头开始。
 * - `continue`：松开冻结当前进度，再次按住从该进度继续。
 */
export const QTE_LONG_PRESS_INTERRUPT_BEHAVIORS = ['reset', 'continue'];
/** 长按 QTE 默认中断行为：重置进度，与属性面板默认项保持一致。 */
export const DEFAULT_QTE_LONG_PRESS_INTERRUPT_BEHAVIOR = 'reset';
export const QTE_SLIDE_DIRECTIONS = [
    'up',
    'down',
    'left',
    'right',
    'up-left',
    'down-left',
    'up-right',
    'down-right',
];
/** 滑动 QTE 默认方向：向右，与新建事件的默认配置一致。 */
export const DEFAULT_QTE_SLIDE_DIRECTION = 'right';
/** QTE 事件的运行配置。 */
export const qteEventConfigSchema = z.object({
    interaction: z.enum(['click', 'rapid-click', 'long-press', 'slide']),
    /** QTE 限时操作毫秒；null 表示不限时，出现时间 +1ms 即过场消失、视频继续播放。 */
    durationMs: z.number().int().nullable(),
    /**
     * 超时规则：不跳转（默认）或 超时自动跳转。
     * 超时自动跳转沿用「到期判定失败并按失败结果继续」的既有语义；
     * 不跳转且开启暂停时可暂停等待玩家继续完成；关闭暂停时到期按失败结果结算并隐藏 QTE。
     * 历史文档没有该字段，按默认值回落，口径与选项事件一致。
     */
    timeoutAction: z.enum(INTERACTION_TIMEOUT_ACTIONS).catch(DEFAULT_INTERACTION_TIMEOUT_ACTION),
    /**
     * 超时跳转的目标剧情节点 ID；仅在「超时自动跳转」下生效。
     * 与选项事件同名字段口径一致：目标直接取自事件配置，不经分支与连线路由；
     * null 表示未单独指定超时去向，回落到失败结果自身的跳转目标。
     */
    timeoutJumpTargetNodeId: z.string().nullable().default(null),
    targetCount: z.number().int(),
    showProgress: z.boolean(),
    pauseVideo: z.boolean(),
    /** 显示给玩家的 QTE 引导文案。 */
    description: z.string().default(''),
    /** 输入音效开关（点击/单点/按住/滑动）；素材按类型取项目设置 eventAssets。 */
    inputSoundEnabled: z.boolean().default(false),
    /** 完成音效开关（连点/长按）；素材按类型取项目设置 eventAssets。 */
    completeSoundEnabled: z.boolean().default(false),
    /** QTE 中心点及占用尺寸，均使用画布百分比坐标。 */
    layout: percentLayoutSchema.default({ x: 50, y: 70, width: 34, height: 22 }),
    /** QTE 引导文字的独立布局；null 表示从 QTE 块位置自动派生（旧数据兼容）。 */
    descriptionLayout: percentLayoutSchema.nullable().default(null),
    /** QTE 倒计时条的独立布局；null 表示从 QTE 块位置自动派生（旧数据兼容）。 */
    countdownLayout: percentLayoutSchema.nullable().default(null),
    /** 可选的 QTE 主视觉素材；素材实体本身不写入项目文档。 */
    visualAssetId: z.string().nullable().default(null),
    /** 玩家输入方式；滑动 QTE 固定由指针输入完成。 */
    inputMethod: z.enum(['pointer', 'keyboard']).default('pointer'),
    /**
     * 键盘输入时匹配的规范化组合键；指针输入时保留默认值。
     *
     * 单个键写成 `s` / `space` / `enter`，组合键用 `+` 连接写成 `ctrl+s`；
     * token 口径见 shared/lib/keyboard/keyboardCombo，修饰键按 ctrl→shift→alt→meta 排序。
     */
    keyboardKey: z.string().min(1).default('space'),
    /** 每次有效输入播放的音效素材。 */
    inputSoundAssetId: z.string().nullable().default(null),
    /** QTE 成功时播放的音效素材。 */
    successSoundAssetId: z.string().nullable().default(null),
    /** QTE 激活期间的事件级帧循环；null 表示只使用标识轨循环。 */
    frameLoop: z
        .object({
        startMs: z.number().int(),
        endMs: z.number().int(),
    })
        .nullable()
        .default(null),
    /**
     * 连点 QTE 的成功模式：按次数或积攒能量。
     *
     * 历史文档没有该字段时回落到「积攒能量」，与既有能量三参数的口径保持连续。
     */
    rapidClickMode: z.enum(QTE_RAPID_CLICK_MODES).default(DEFAULT_QTE_RAPID_CLICK_MODE),
    /** 连击 QTE 每次输入增加的能量值。 */
    energyPerInput: z.number().int().min(1).default(15),
    /** 连击 QTE 每秒消耗的能量值。 */
    energyDecayPerSecond: z.number().int().min(0).default(50),
    /** 连击 QTE 达到该能量值时判定为成功。 */
    successEnergy: z.number().int().min(1).default(100),
    /** 长按 QTE 必须连续按住的时长。 */
    holdDurationMs: z.number().int().min(1).default(1_500),
    /**
     * 长按中断行为：松开后重置进度或冻结并继续。
     * 历史文档没有该字段时按「重置进度」回落，保证长按必须连续完成。
     */
    longPressInterruptBehavior: z
        .enum(QTE_LONG_PRESS_INTERRUPT_BEHAVIORS)
        .default(DEFAULT_QTE_LONG_PRESS_INTERRUPT_BEHAVIOR),
    /** 滑动 QTE 的目标方向。 */
    slideDirection: z.enum(QTE_SLIDE_DIRECTIONS).default(DEFAULT_QTE_SLIDE_DIRECTION),
    /** 滑动 QTE 从起点到终点的最小位移距离。 */
    minimumSlideDistancePx: z.number().int().min(1).default(120),
    /** 滑动 QTE 是否显示轨迹提示图；关闭时隐藏 qteSwipe.effectAssetId。 */
    showSlideTrail: z.boolean().default(true),
    /** 成功与失败结果的运行表现；分支跳转由 outcome.branchId 是否连接边自然决定。 */
    outcomeEffects: z
        .object({
        success: z.object({
            /** 成功后是否切换到 outcome 绑定的后续视频/节点。 */
            switchVideo: z.boolean().default(false),
            /** 成功后是否触发屏幕震动。 */
            screenShake: z.boolean().default(false),
        }),
        failure: z.object({
            /** 失败后是否切换到 outcome 绑定的后续视频/节点。 */
            switchVideo: z.boolean().default(false),
            /** 失败后是否触发屏幕震动。 */
            screenShake: z.boolean().default(false),
        }),
    })
        .default({
        success: { switchVideo: false, screenShake: false },
        failure: { switchVideo: false, screenShake: false },
    }),
    /** QTE 引导文案的字体样式；文案样式属于事件内容，随 events_json 保存。 */
    descriptionTextStyle: textStyleSchema.default(DEFAULT_QTE_DESCRIPTION_TEXT_STYLE),
    /** 素材位的应用范围；只作用于 UI 样式（event_assets_json），文案样式不受它影响。 */
    styleScope: z.enum(TEXT_STYLE_SCOPES).catch(DEFAULT_TEXT_STYLE_SCOPE),
    /** 「用于指定篇章」时目标章节 ID；其他范围下为 null。 */
    styleScopeChapterId: z.string().nullable().default(null),
});
/** QTE 事件的成败结果。 */
export const qteOutcomeSchema = z.object({
    id: z.string(),
    role: z.enum(['success', 'failure']),
    label: z.string(),
    branchId: z.string().nullable(),
    /** 该结果成立后要修改的项目数值动作列表；空数组表示不修改任何数值（默认）。 */
    variableEffects: optionVariableEffectsSchema.default([]),
    /** 该结果成立后的跳转时机；缺字段按默认「完播」处理。 */
    trigger: branchTriggerSchema.optional(),
});
/** 选项事件完整结构。 */
export const choiceEventSchema = z.object({
    id: z.string(),
    type: z.literal('choice'),
    title: z.string(),
    anchor: eventAnchorSchema,
    config: choiceEventConfigSchema,
    /** 选项内容与路由；不保存布局。 */
    options: z.array(eventOptionSchema),
    /** 选项位置/尺寸，按 optionId 索引；与 descriptionLayout/countdownLayout 同一套语义。 */
    optionLayouts: z.record(z.string(), percentLayoutSchema).default({}),
});
/** QTE 事件完整结构。 */
export const qteEventSchema = z.object({
    id: z.string(),
    type: z.literal('qte'),
    title: z.string(),
    anchor: eventAnchorSchema,
    config: qteEventConfigSchema,
    outcomes: z.array(qteOutcomeSchema),
});
/** 章节剧本来源文件，按文档规范只保存素材 ID 与文件元信息。 */
const chapterScriptSourceSchema = z.object({
    assetId: z.string(),
    fileName: z.string(),
    version: z.number().int(),
    status: z.enum(['uploaded', 'parsing', 'parsed', 'failed']),
});
/** 被动触发事件，配置细节后续版本再收紧。 */
const passiveEventSchema = z.object({
    id: z.string(),
    type: z.literal('passive'),
    title: z.string(),
    anchor: eventAnchorSchema,
    config: extensibleObjectSchema,
});
/** 覆盖层事件，配置细节后续版本再收紧。 */
const overlayEventSchema = z.object({
    id: z.string(),
    type: z.literal('overlay'),
    title: z.string(),
    anchor: eventAnchorSchema,
    config: extensibleObjectSchema,
});
/** 节点互动事件按类型判别，choice 与 qte 已收紧为完整协议。 */
export const nodeEventSchema = z.discriminatedUnion('type', [
    choiceEventSchema,
    qteEventSchema,
    passiveEventSchema,
    overlayEventSchema,
]);
/** 帧循环标识轨元素。 */
export const frameLoopMarkerItemSchema = z.object({
    id: z.string(),
    startMs: z.number().int(),
    endMs: z.number().int(),
    label: z.string(),
    displayOnHover: z.boolean(),
});
/** 视频轨元素。 */
export const videoTrackItemSchema = z.object({
    id: z.string(),
    assetId: z.string(),
    startMs: z.number().int(),
    sourceInMs: z.number().int(),
    sourceOutMs: z.number().int(),
    volume: z.number(),
    muted: z.boolean(),
});
/** 互动轨元素，展示位置由 event.anchor 派生，不保存时间。 */
export const interactionTrackItemSchema = z.object({
    id: z.string(),
    eventId: z.string(),
    displayBranchIds: z.array(z.string()),
});
/** QTE 轨元素，展示位置由 event.anchor 派生，不保存时间。统一使用 eventId 引用事件。 */
export const qteTrackItemSchema = z.object({
    id: z.string(),
    eventId: z.string(),
    successBranchId: z.string().nullable(),
    failureBranchId: z.string().nullable(),
    screenState: extensibleObjectSchema,
});
/**
 * 轨道编辑状态字段：锁定阻止片段编辑，隐藏让轨道内容退出播放引擎。
 * 历史文档缺失时按 false 兜底，属于编辑意图，随文档持久化。
 */
const trackEditStateSchema = {
    isHidden: z.boolean().default(false),
    isLocked: z.boolean().default(false),
};
/** 时间轴轨道按类别判别，各自持有专用元素协议。 */
export const timelineTrackSchema = z.discriminatedUnion('type', [
    z.object({
        id: z.string(),
        type: z.literal('frame-loop-marker'),
        name: z.string(),
        items: z.array(frameLoopMarkerItemSchema),
        ...trackEditStateSchema,
    }),
    z.object({
        id: z.string(),
        type: z.literal('video'),
        name: z.string(),
        items: z.array(videoTrackItemSchema),
        ...trackEditStateSchema,
    }),
    z.object({
        id: z.string(),
        type: z.literal('interaction'),
        name: z.string(),
        items: z.array(interactionTrackItemSchema),
        ...trackEditStateSchema,
    }),
    z.object({
        id: z.string(),
        type: z.literal('qte'),
        name: z.string(),
        items: z.array(qteTrackItemSchema),
        ...trackEditStateSchema,
    }),
]);
/** 项目文档中的节点时间轴。 */
export const timelineSchema = z.object({
    id: z.string(),
    durationMs: z.number().int(),
    tracks: z.array(timelineTrackSchema),
});
/** 项目文档中的剧情分支，目标节点由 chapter.edges[] 表达。 */
const nodeBranchSchema = z.object({
    id: z.string(),
    type: z.enum(['choice', 'qte-success', 'qte-failure', 'timeout', 'auto']),
    label: z.string(),
    eventId: z.string().nullable(),
    optionId: z.string().nullable(),
    outcomeId: z.string().nullable(),
    condition: extensibleObjectSchema.nullable(),
});
/** 章节连接边：分支与目标节点的唯一关联。 */
const chapterEdgeSchema = z.object({
    id: z.string(),
    branchId: z.string(),
    targetChapterId: z.string(),
    targetNodeId: z.string(),
});
/**
 * 跨篇章跳转目标：跳转节点（type = placeholder）结束播放后要切到的章节与节点。
 * 普通连线（chapter.edges[]）v0.1 只允许章内连接，跨篇章的唯一载体就是这个节点级字段。
 */
const nodeJumpTargetSchema = z.object({
    chapterId: z.string(),
    nodeId: z.string(),
});
/** 项目文档中的剧情节点。 */
const projectNodeSchema = z.object({
    id: z.string(),
    type: z.enum(['scene', 'ending', 'placeholder', 'logic']),
    title: z.string(),
    /** 业务编号（如 3-4）；仅编辑器工作副本携带，保存仍以 cg_script_node.node_code 为准。 */
    nodeCode: z.string().nullable().optional(),
    /** 当前节点剧本正文；仅编辑器工作副本携带，持久化事实仍是 cg_script_node.content。 */
    content: z.string().optional(),
    description: z.string(),
    status: z.enum(['draft', 'ready', 'broken']),
    scriptBlockIds: z.array(z.string()),
    timeline: timelineSchema,
    events: z.array(nodeEventSchema),
    branches: z.array(nodeBranchSchema),
    /**
     * 跳转节点配置的跨篇章目标；非跳转节点或跳转节点未配置目标时缺失或为 null。
     * 允许缺失以兼容历史文档与编辑器构造的节点对象。
     */
    jumpTarget: nodeJumpTargetSchema.nullable().optional(),
    /**
     * 结局档位：好结局 / 普通结局 / 坏结局。
     *
     * 由后端在结局节点上下发，播放端据此展示达成的是哪一档结局；
     * 非结局节点或历史快照缺失时按普通结局兜底。
     */
    endingKind: z.enum(['GOOD', 'NORMAL', 'BAD']).nullable().optional(),
    /**
     * 节点级事件素材（选项按钮与各类 QTE 的表现图/音效）：唯一来源是 cg_story_node.event_assets_json，
     * 生成节点时由后端写入完整默认值；历史快照缺失时播放端回退项目设置分区或内置空素材。
     * 事件素材 schema 定义在设置分区之后，这里用 lazy 规避声明顺序。
     */
    eventAssets: z.lazy(() => eventAssetsSchema).optional(),
    /**
     * 本节点出口连线的原始副本（cg_story_node.connections_json 逐条原文）。
     *
     * 只存在于编辑器工作副本：编辑器改写某个出口的跳转目标时，必须整体回写这份数组，
     * 保留未编辑出口的 kind/required/condition/points 等字段。播放文档不从 ProjectDocument
     * 读取该字段（播放端只消费 chapters[].edges），加载失败或缺失时按空数组处理。
     */
    connections: z.array(extensibleObjectSchema).optional(),
});
/** 项目文档中的章节剧本。 */
const chapterScriptSchema = z.object({
    source: chapterScriptSourceSchema.nullable(),
    blocks: z.array(z.object({
        id: z.string(),
        text: z.string(),
        order: z.number().int(),
    })),
});
/** 追加项目文档校验错误，统一保持路径和提示可定位。 */
function addDocumentIssue(ctx, path, message) {
    ctx.addIssue({
        code: 'custom',
        path,
        message,
    });
}
/** 校验单个时间轴的轨道结构，确保系统轨道数量与类型符合文档规范。 */
function validateNodeTimeline(chapterIndex, nodeIndex, timeline, ctx) {
    /** 当前节点时间轴按类型分组后的轨道数量。 */
    const trackCountByType = timeline.tracks.reduce((counts, track) => {
        counts[track.type] = (counts[track.type] ?? 0) + 1;
        return counts;
    }, {});
    if (trackCountByType['frame-loop-marker'] !== 1) {
        addDocumentIssue(ctx, ['chapters', chapterIndex, 'nodes', nodeIndex, 'timeline', 'tracks'], '每个节点时间轴必须且只能有一条帧循环标识轨');
    }
    if (trackCountByType.video < 1) {
        addDocumentIssue(ctx, ['chapters', chapterIndex, 'nodes', nodeIndex, 'timeline', 'tracks'], '每个节点时间轴至少需要一条视频轨');
    }
    if (trackCountByType.interaction < 1) {
        addDocumentIssue(ctx, ['chapters', chapterIndex, 'nodes', nodeIndex, 'timeline', 'tracks'], '每个节点时间轴至少需要一条互动交互轨');
    }
    if (trackCountByType.qte < 1) {
        addDocumentIssue(ctx, ['chapters', chapterIndex, 'nodes', nodeIndex, 'timeline', 'tracks'], '每个节点时间轴至少需要一条 QTE 轨');
    }
}
/** 校验章节内节点、分支、边与事件的跨字段引用关系。 */
function validateProjectDocument(document, ctx) {
    /** 全局章节索引，支持起始章节引用与章节级唯一性校验。 */
    const chapterById = new Map();
    /** 全局节点索引，支持 rootNodeId、startNodeId 和边引用校验。 */
    const nodeById = new Map();
    /** 全局分支索引，支持 edge 与事件分支的双向引用校验。 */
    const branchById = new Map();
    document.chapters.forEach((chapter, chapterIndex) => {
        if (chapterById.has(chapter.id)) {
            addDocumentIssue(ctx, ['chapters', chapterIndex, 'id'], '章节 ID 在项目文档内必须唯一');
        }
        else {
            chapterById.set(chapter.id, chapter);
        }
        if (chapter.rootNodeId !== null &&
            !chapter.nodes.some((node) => node.id === chapter.rootNodeId)) {
            addDocumentIssue(ctx, ['chapters', chapterIndex, 'rootNodeId'], 'rootNodeId 必须引用当前章节中的剧情节点或为 null');
        }
        chapter.nodes.forEach((node, nodeIndex) => {
            if (nodeById.has(node.id)) {
                addDocumentIssue(ctx, ['chapters', chapterIndex, 'nodes', nodeIndex, 'id'], '节点 ID 在项目文档内必须唯一');
            }
            else {
                nodeById.set(node.id, { chapterIndex, nodeIndex, chapterId: chapter.id });
            }
            validateNodeTimeline(chapterIndex, nodeIndex, node.timeline, ctx);
            if (node.type === 'ending' && node.branches.length > 0) {
                addDocumentIssue(ctx, ['chapters', chapterIndex, 'nodes', nodeIndex, 'branches'], '结局节点不能包含分支');
            }
            node.events.forEach((event, eventIndex) => {
                if (event.anchor.mode === 'clip-relative') {
                    if (!event.anchor.clipId) {
                        addDocumentIssue(ctx, [
                            'chapters',
                            chapterIndex,
                            'nodes',
                            nodeIndex,
                            'events',
                            eventIndex,
                            'anchor',
                            'clipId',
                        ], 'clip-relative 事件必须绑定视频片段 ID');
                    }
                    else if (!node.timeline.tracks.some((track) => track.type === 'video' &&
                        track.items.some((item) => item.id === event.anchor.clipId))) {
                        addDocumentIssue(ctx, [
                            'chapters',
                            chapterIndex,
                            'nodes',
                            nodeIndex,
                            'events',
                            eventIndex,
                            'anchor',
                            'clipId',
                        ], 'clip-relative 事件的 clipId 必须引用当前节点的视频片段');
                    }
                }
                else if (event.anchor.clipId !== null) {
                    addDocumentIssue(ctx, [
                        'chapters',
                        chapterIndex,
                        'nodes',
                        nodeIndex,
                        'events',
                        eventIndex,
                        'anchor',
                        'clipId',
                    ], 'sequence-absolute 事件的 clipId 必须为 null');
                }
                if (event.anchor.offsetMs < 0) {
                    addDocumentIssue(ctx, [
                        'chapters',
                        chapterIndex,
                        'nodes',
                        nodeIndex,
                        'events',
                        eventIndex,
                        'anchor',
                        'offsetMs',
                    ], '事件锚点偏移必须大于等于 0');
                }
            });
            node.branches.forEach((branch, branchIndex) => {
                if (branchById.has(branch.id)) {
                    addDocumentIssue(ctx, ['chapters', chapterIndex, 'nodes', nodeIndex, 'branches', branchIndex, 'id'], '分支 ID 在项目文档内必须唯一');
                    return;
                }
                branchById.set(branch.id, {
                    chapterIndex,
                    nodeIndex,
                    branchIndex,
                    chapterId: chapter.id,
                    nodeId: node.id,
                    branch,
                });
                const branchPath = ['chapters', chapterIndex, 'nodes', nodeIndex, 'branches', branchIndex];
                if (branch.type !== 'auto' && !branch.eventId) {
                    addDocumentIssue(ctx, [...branchPath, 'eventId'], '非 auto 分支必须绑定事件 ID');
                }
                if (branch.type === 'auto' && branch.eventId !== null) {
                    addDocumentIssue(ctx, [...branchPath, 'eventId'], 'auto 分支的 eventId 必须为 null');
                }
                if (branch.type === 'choice') {
                    if (branch.optionId === null) {
                        addDocumentIssue(ctx, [...branchPath, 'optionId'], 'choice 分支必须绑定 optionId');
                    }
                    if (branch.outcomeId !== null) {
                        addDocumentIssue(ctx, [...branchPath, 'outcomeId'], 'choice 分支的 outcomeId 必须为 null');
                    }
                }
                if (branch.type === 'qte-success' || branch.type === 'qte-failure') {
                    if (branch.optionId !== null) {
                        addDocumentIssue(ctx, [...branchPath, 'optionId'], 'QTE 分支的 optionId 必须为 null');
                    }
                    if (branch.outcomeId === null) {
                        addDocumentIssue(ctx, [...branchPath, 'outcomeId'], 'QTE 分支必须绑定 outcomeId');
                    }
                }
                if (branch.type === 'timeout') {
                    if (branch.optionId !== null) {
                        addDocumentIssue(ctx, [...branchPath, 'optionId'], 'timeout 分支的 optionId 必须为 null');
                    }
                    if (branch.outcomeId !== null) {
                        addDocumentIssue(ctx, [...branchPath, 'outcomeId'], 'timeout 分支的 outcomeId 必须为 null');
                    }
                }
            });
        });
        chapter.edges.forEach((edge, edgeIndex) => {
            const edgePath = ['chapters', chapterIndex, 'edges', edgeIndex];
            const branch = branchById.get(edge.branchId);
            if (!branch) {
                addDocumentIssue(ctx, [...edgePath, 'branchId'], 'edge.branchId 必须引用真实分支');
                return;
            }
            if (edge.targetChapterId !== chapter.id) {
                addDocumentIssue(ctx, [...edgePath, 'targetChapterId'], 'v0.1 不允许跨章节连线，targetChapterId 必须等于当前章节 ID');
            }
            const targetNode = chapter.nodes.find((node) => node.id === edge.targetNodeId);
            if (!targetNode) {
                addDocumentIssue(ctx, [...edgePath, 'targetNodeId'], 'targetNodeId 必须引用当前章节中的节点');
                return;
            }
            if (targetNode.id === branch.nodeId) {
                addDocumentIssue(ctx, [...edgePath, 'targetNodeId'], '分支不能连回自身所属节点');
            }
        });
    });
    if (document.settings.startChapterId || document.settings.startNodeId) {
        if (!document.settings.startChapterId || !document.settings.startNodeId) {
            addDocumentIssue(ctx, ['settings'], 'startChapterId 和 startNodeId 必须同时设置或同时留空');
        }
    }
    if (document.settings.startChapterId && document.settings.startNodeId) {
        const startChapter = chapterById.get(document.settings.startChapterId);
        if (!startChapter) {
            addDocumentIssue(ctx, ['settings', 'startChapterId'], 'startChapterId 必须引用真实章节');
        }
        else {
            const startNode = nodeById.get(document.settings.startNodeId);
            if (!startNode) {
                addDocumentIssue(ctx, ['settings', 'startNodeId'], 'startNodeId 必须引用真实节点');
            }
            else if (startNode.chapterId !== startChapter.id) {
                addDocumentIssue(ctx, ['settings', 'startNodeId'], 'startNodeId 必须属于 startChapterId 指定的章节');
            }
        }
    }
    branchById.forEach((branchRef) => {
        const { branch, chapterIndex, nodeIndex, branchIndex, nodeId } = branchRef;
        const branchPath = ['chapters', chapterIndex, 'nodes', nodeIndex, 'branches', branchIndex];
        const sourceNode = nodeById.get(nodeId);
        if (!sourceNode)
            return;
        const sourceNodeEntity = document.chapters[sourceNode.chapterIndex].nodes[sourceNode.nodeIndex];
        const sourceEvent = sourceNodeEntity.events.find((event) => event.id === branch.eventId);
        if (!sourceEvent && branch.type !== 'auto') {
            addDocumentIssue(ctx, [...branchPath, 'eventId'], '分支必须引用当前节点中的真实事件');
            return;
        }
        if (branch.type === 'choice') {
            if (sourceEvent?.type !== 'choice') {
                addDocumentIssue(ctx, [...branchPath, 'eventId'], 'choice 分支必须绑定 choice 事件');
                return;
            }
            const option = sourceEvent.options.find((item) => item.id === branch.optionId);
            if (!option) {
                addDocumentIssue(ctx, [...branchPath, 'optionId'], 'choice 分支必须绑定当前事件中的真实选项');
            }
        }
        if (branch.type === 'qte-success' || branch.type === 'qte-failure') {
            if (sourceEvent?.type !== 'qte') {
                addDocumentIssue(ctx, [...branchPath, 'eventId'], 'QTE 分支必须绑定 qte 事件');
                return;
            }
            const outcome = sourceEvent.outcomes.find((item) => item.id === branch.outcomeId);
            if (!outcome) {
                addDocumentIssue(ctx, [...branchPath, 'outcomeId'], 'QTE 分支必须绑定当前事件中的真实结果');
            }
            else if (outcome.role !== branch.type.split('-')[1]) {
                addDocumentIssue(ctx, [...branchPath, 'outcomeId'], 'QTE 分支类型必须与结果角色一致');
            }
        }
        if (branch.type === 'timeout') {
            if (sourceEvent?.type !== 'choice') {
                addDocumentIssue(ctx, [...branchPath, 'eventId'], 'timeout 分支必须绑定 choice 事件');
            }
            else if (sourceEvent.config.timeoutAction !== 'jump-node') {
                addDocumentIssue(ctx, [...branchPath, 'eventId'], 'timeout 分支只允许对应 jump-node 超时规则');
            }
        }
    });
    document.chapters.forEach((chapter, chapterIndex) => {
        const adjacency = new Map();
        chapter.edges.forEach((edge) => {
            const branch = branchById.get(edge.branchId);
            if (branch?.chapterId !== chapter.id)
                return;
            const targets = adjacency.get(branch.nodeId) ?? [];
            targets.push(edge.targetNodeId);
            adjacency.set(branch.nodeId, targets);
        });
        /** 深度优先遍历当前章节的有向连线，检测回边形成的环。 */
        const visited = new Set();
        const active = new Set();
        const hasCycleFrom = (nodeId) => {
            if (active.has(nodeId))
                return true;
            if (visited.has(nodeId))
                return false;
            visited.add(nodeId);
            active.add(nodeId);
            const nextNodes = adjacency.get(nodeId) ?? [];
            for (const nextNodeId of nextNodes) {
                if (hasCycleFrom(nextNodeId))
                    return true;
            }
            active.delete(nodeId);
            return false;
        };
        for (const node of chapter.nodes) {
            if (hasCycleFrom(node.id)) {
                addDocumentIssue(ctx, ['chapters', chapterIndex, 'edges'], '当前章节的连接边不能形成有向环');
                break;
            }
        }
    });
}
/** 项目文档中的章节。 */
const projectChapterSchema = z.object({
    id: z.string(),
    title: z.string(),
    order: z.number().int(),
    status: z.enum(['draft', 'ready', 'published']),
    revision: z.number().int(),
    rootNodeId: z.string().nullable(),
    script: chapterScriptSchema,
    plotTreeCanvas: z.object({
        id: z.string(),
        zoom: z.number(),
        viewport: z.object({ x: z.number(), y: z.number() }),
        nodeLayouts: z.record(z.string(), z.object({ x: z.number(), y: z.number() })),
        branchLayouts: z.record(z.string(), extensibleObjectSchema),
    }),
    nodes: z.array(projectNodeSchema),
    edges: z.array(chapterEdgeSchema),
});
/** QTE 长按环形进度的默认未完成颜色（灰色）。 */
export const QTE_RING_INCOMPLETE_COLOR_DEFAULT = '#808080';
/** QTE 长按环形进度的默认完成颜色（品牌紫色）。 */
export const QTE_RING_COMPLETE_COLOR_DEFAULT = '#8448ff';
/**
 * 事件交互素材：为选项按钮与各类 QTE 事件配置表现图片与音效，
 * 由设置页「事件素材」页签维护，全部字段均可为空（未选择）。
 * 字段为后续增补，历史上已入库的文档缺失该分区时按默认值兜底，避免整份文档解析失败。
 */
export const eventAssetsSchema = z.object({
    // 选项按钮三态图：未选中 / 选中 / 按下；进度条横向进度图与倒计时标记归属选项分组，
    // 两项为增补字段，历史文档缺失时按 null 兜底。
    option: z.object({
        idleAssetId: z.string().nullable(),
        selectedAssetId: z.string().nullable(),
        pressedAssetId: z.string().nullable(),
        progressBarAssetId: z.string().nullable().catch(null),
        countdownMarkerAssetId: z.string().nullable().catch(null),
    }),
    // QTE-点击：默认图、点击图、单击音效。
    qteTap: z.object({
        defaultAssetId: z.string().nullable(),
        pressedAssetId: z.string().nullable(),
        tapAudioAssetId: z.string().nullable(),
    }),
    // QTE-连点：默认图、点击图、单点音效与完成音效。
    qteMultiTap: z.object({
        defaultAssetId: z.string().nullable(),
        pressedAssetId: z.string().nullable(),
        tapAudioAssetId: z.string().nullable(),
        completeAudioAssetId: z.string().nullable(),
    }),
    // QTE-长按：默认图、按住图、按住音效与完成音效；完成音效与环形进度颜色为增补字段，
    // 历史文档缺失时分别按 null 与默认色（灰/紫）兜底。
    qteLongPress: z.object({
        defaultAssetId: z.string().nullable(),
        holdingAssetId: z.string().nullable(),
        holdAudioAssetId: z.string().nullable(),
        completeAudioAssetId: z.string().nullable().catch(null),
        ringIncompleteColor: z.string().catch(QTE_RING_INCOMPLETE_COLOR_DEFAULT),
        ringCompleteColor: z.string().catch(QTE_RING_COMPLETE_COLOR_DEFAULT),
    }),
    // QTE-滑动：默认图、滑动特效（视频素材）与滑动音效。
    qteSwipe: z.object({
        defaultAssetId: z.string().nullable(),
        effectAssetId: z.string().nullable(),
        swipeAudioAssetId: z.string().nullable(),
    }),
});
/**
 * 返回一个事件素材默认值：各类素材位全部为空，颜色位带默认色，
 * 供 schema 兜底与新建节点使用。文案样式不在这里，它属于事件内容，随 events_json 保存。
 */
export function createEmptyEventAssets() {
    return {
        option: {
            idleAssetId: null,
            selectedAssetId: null,
            pressedAssetId: null,
            progressBarAssetId: null,
            countdownMarkerAssetId: null,
        },
        qteTap: { defaultAssetId: null, pressedAssetId: null, tapAudioAssetId: null },
        qteMultiTap: {
            defaultAssetId: null,
            pressedAssetId: null,
            tapAudioAssetId: null,
            completeAudioAssetId: null,
        },
        qteLongPress: {
            defaultAssetId: null,
            holdingAssetId: null,
            holdAudioAssetId: null,
            completeAudioAssetId: null,
            ringIncompleteColor: QTE_RING_INCOMPLETE_COLOR_DEFAULT,
            ringCompleteColor: QTE_RING_COMPLETE_COLOR_DEFAULT,
        },
        qteSwipe: { defaultAssetId: null, effectAssetId: null, swipeAudioAssetId: null },
    };
}
/** 画布比例标识的合法集合：编辑器比例菜单与播放层舞台共用，标识本身即"宽:高"。 */
export const CANVAS_RATIO_IDS = [
    '16:9',
    '4:3',
    '18:9',
    '21:9',
    '9:16',
    '3:4',
    '1:1',
    '9:18',
];
/** 画面比例的兜底值：cg_project.screen_ratio 缺失或非法时使用。 */
export const DEFAULT_CANVAS_RATIO_ID = '16:9';
/** 把画布比例标识解析成数值宽高比（宽 / 高），缺失或异常标识兜底 16:9。 */
export function resolveCanvasAspectRatio(ratioId) {
    const [width, height] = (ratioId ?? '').split(':').map(Number);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return 16 / 9;
    }
    return width / height;
}
/**
 * 会话音频运行默认值：settings_json 不再持久化 audio 分区，读取方按此兜底。
 */
export const DEFAULT_PROJECT_AUDIO_SETTINGS = { defaultVolume: 1, allowMute: true };
/** 音效运行默认值：开关开启、音量 80；position 仅用于预览控件摆放。 */
export const DEFAULT_PROJECT_SFX_SETTINGS = {
    enabled: true,
    volume: 80,
    position: { x: 0, y: 0 },
};
/**
 * 载入页文案默认值：创作者留空时按它补全，与播放层兜底文案保持一致。
 */
export const DEFAULT_LOADING_PAGE_TEXTS = {
    title: '正在进入游戏',
};
/**
 * 开始页文案默认值：标题、副标题与开始按钮文案都有默认值，
 * 创作者打开设置时不再是三个空字段，保存后玩家看到的也就是这些文案。
 */
export const DEFAULT_START_PAGE_TEXTS = {
    title: '未命名影游',
    subtitle: '一场关于选择与命运的互动故事',
    buttonText: '开始游戏',
    chapterEntryText: '章节选择',
    exitButtonText: '退出',
};
/**
 * 游戏页面文案的默认文本样式。
 *
 * 字号、字间距与行间距都按 1920×1080 设计基准的像素值记录：
 * 开始页是整屏画面，播放端与编辑器画布都按「渲染宽度 ÷ 1920」等比换算，
 * 与选项文案用画布宽度百分比的口径等价，但创作者看到的是设计稿上的真实像素。
 * 各文案位的默认字号统一为 24px：标题、按钮、跳过入口都从同一个起点开始调。
 */
export const DEFAULT_START_BUTTON_TEXT_STYLE = {
    fontFamily: DEFAULT_TEXT_STYLE_FONT_FAMILY,
    fontSize: 24,
    color: DEFAULT_TEXT_STYLE_COLOR,
    letterSpacing: 0,
    lineHeight: 0,
};
/** 前置 CG 播放完成后的动作：进入开始页 / 继续播放下一个视频。 */
export const INTRO_ANIMATION_AFTER_COMPLETE = ['start-page', 'next-video'];
/**
 * 前置 CG 跳过按钮的默认文案。
 *
 * 文案是纯文本，末尾的箭头属于文案本身而不是代码写死的装饰；
 * 作者可以在游戏页面编辑器里整段改写，也可以把箭头删掉。
 */
export const DEFAULT_INTRO_SKIP_TEXT = '跳过 CG ›';
/**
 * 前置 CG（动画）的默认配置。
 *
 * 默认不配置素材（播放端跳过本步直接进入开始页）、播完进入开始页、允许跳过、
 * 每次进入游戏都播放一次，跳过按钮使用默认文案。
 */
export const DEFAULT_INTRO_ANIMATION = {
    assetId: null,
    afterComplete: 'start-page',
    allowSkip: true,
    firstTimeOnly: false,
    skipText: DEFAULT_INTRO_SKIP_TEXT,
};
/** 游戏页面步骤标识：载入页 / 前置动画 / 开始页。 */
export const GAME_PAGE_STEP_IDS = ['loading', 'intro', 'start'];
/**
 * 游戏页面默认顺序。
 *
 * 新项目默认只放载入页：载入页是进入游戏的必经过渡，前置 CG 与开始页由作者按需从左侧拖进顺序条。
 * 已经保存过顺序的项目一律沿用自己存的值，不受这里影响。
 */
export const DEFAULT_GAME_PAGE_ORDER = ['loading'];
/**
 * 游戏页面画布上可编辑的元素标识。
 *
 * 这些元素在编辑器画布上可以直接拖动与缩放，位置和尺寸随项目设置一起保存。
 * 标识按「页面前缀 + 元素」命名，避免三个页面里的同名元素（例如都叫标题）互相顶掉；
 * 存储时分别落在各页面自己的 elementLayouts 里，见下面三张默认布局表。
 */
export const GAME_PAGE_CANVAS_ELEMENT_IDS = [
    'startChapterEntry',
    'startTitle',
    'startSubtitle',
    'startButton',
    'startExitButton',
    'loadingTitle',
    'loadingProgress',
    'introSkip',
];
/**
 * 开始页元素默认布局：与未启用拖拽前的居中排布保持一致，
 * x/y 为元素左上角相对画布宽高的百分比，width/height 同样是百分比（与 percentLayoutSchema 同语义）。
 */
export const DEFAULT_START_PAGE_ELEMENT_LAYOUTS = {
    chapterEntry: { x: 2.5, y: 3, width: 12, height: 7 },
    title: { x: 20, y: 40, width: 60, height: 10 },
    subtitle: { x: 20, y: 51, width: 60, height: 6 },
    button: { x: 41, y: 58, width: 18, height: 8 },
    exitButton: { x: 78, y: 84, width: 14, height: 6 },
};
/** 载入页元素默认布局：标题与进度条。 */
export const DEFAULT_LOADING_PAGE_ELEMENT_LAYOUTS = {
    title: { x: 41.868, y: 70.178, width: 24.066, height: 7.814 },
    progress: { x: 31.489, y: 85.078, width: 47.29, height: 2.409 },
};
/** 前置动画元素默认布局：跳过按钮。 */
export const DEFAULT_INTRO_ANIMATION_ELEMENT_LAYOUTS = {
    skip: { x: 80, y: 84, width: 14, height: 6 },
};
/** 开始页画布元素布局表：章节选择入口 / 标题 / 副标题 / 开始按钮。 */
export const startPageElementLayoutsSchema = z
    .object({
    chapterEntry: percentLayoutSchema.default({
        ...DEFAULT_START_PAGE_ELEMENT_LAYOUTS.chapterEntry,
    }),
    title: percentLayoutSchema.default({ ...DEFAULT_START_PAGE_ELEMENT_LAYOUTS.title }),
    subtitle: percentLayoutSchema.default({ ...DEFAULT_START_PAGE_ELEMENT_LAYOUTS.subtitle }),
    button: percentLayoutSchema.default({ ...DEFAULT_START_PAGE_ELEMENT_LAYOUTS.button }),
    exitButton: percentLayoutSchema.default({ ...DEFAULT_START_PAGE_ELEMENT_LAYOUTS.exitButton }),
})
    .catch({ ...DEFAULT_START_PAGE_ELEMENT_LAYOUTS });
/** 载入页画布元素布局表。 */
export const loadingPageElementLayoutsSchema = z
    .object({
    title: percentLayoutSchema.default({ ...DEFAULT_LOADING_PAGE_ELEMENT_LAYOUTS.title }),
    progress: percentLayoutSchema.default({ ...DEFAULT_LOADING_PAGE_ELEMENT_LAYOUTS.progress }),
})
    .catch({ ...DEFAULT_LOADING_PAGE_ELEMENT_LAYOUTS });
/** 前置动画画布元素布局表。 */
export const introAnimationElementLayoutsSchema = z
    .object({
    skip: percentLayoutSchema.default({ ...DEFAULT_INTRO_ANIMATION_ELEMENT_LAYOUTS.skip }),
})
    .catch({ ...DEFAULT_INTRO_ANIMATION_ELEMENT_LAYOUTS });
/**
 * 画布元素的外观：圆角、背景色与背景图。
 *
 * 背景图优先：配置了背景图时背景色失效（图铺满元素），清空背景图后背景色重新生效。
 * 圆角按 1920 设计基准的像素值记录，随画布等比缩放；背景色是 CSS 颜色字符串。
 */
export const gamePageElementStyleSchema = z.object({
    /** 圆角（1920 设计基准下的像素值）。 */
    borderRadius: z.number().catch(8),
    /** 背景色；配置了背景图时忽略。 */
    backgroundColor: z.string().catch('transparent'),
    /** 背景图素材引用；为空表示用背景色。 */
    backgroundAssetId: z.string().nullable().catch(null),
});
/** 开始页元素默认外观：胶囊与按钮带半透明底，纯文案位透明。 */
export const DEFAULT_START_PAGE_ELEMENT_STYLES = {
    chapterEntry: {
        borderRadius: 9999,
        backgroundColor: 'rgba(5, 6, 8, 0.62)',
        backgroundAssetId: null,
    },
    title: { borderRadius: 0, backgroundColor: 'transparent', backgroundAssetId: null },
    subtitle: { borderRadius: 0, backgroundColor: 'transparent', backgroundAssetId: null },
    button: { borderRadius: 8, backgroundColor: 'rgba(5, 6, 8, 0.62)', backgroundAssetId: null },
    /** 退出按钮是弱操作：比主按钮更淡的底，避免抢开始按钮的注意力。 */
    exitButton: {
        borderRadius: 8,
        backgroundColor: 'rgba(5, 6, 8, 0.38)',
        backgroundAssetId: null,
    },
};
/** 载入页元素默认外观。 */
export const DEFAULT_LOADING_PAGE_ELEMENT_STYLES = {
    title: { borderRadius: 0, backgroundColor: 'transparent', backgroundAssetId: null },
};
/**
 * 载入页进度条的默认外观。
 *
 * 进度条有自己的三段外观，不复用元素样式的背景图与背景色：
 * 胶囊圆角 + 半透明底轨 + 品牌色动轨，与载入页的深色画面配合。
 */
export const DEFAULT_LOADING_PAGE_PROGRESS_STYLE = {
    borderRadius: 9999,
    railColor: 'rgba(255, 255, 255, 0.12)',
    activeColor: '#8448FF',
};
/**
 * 载入页进度条外观：圆角、底轨颜色与动轨颜色。
 *
 * 低轨（rail）是进度条未填充的底色，动轨（active）是已加载的填充部分。
 */
export const loadingPageProgressStyleSchema = z
    .object({
    /** 圆角（1920 设计基准下的像素值）。 */
    borderRadius: z.number().catch(DEFAULT_LOADING_PAGE_PROGRESS_STYLE.borderRadius),
    /** 底轨颜色：进度条未填充部分的底色。 */
    railColor: z.string().catch(DEFAULT_LOADING_PAGE_PROGRESS_STYLE.railColor),
    /** 动轨颜色：已加载部分的填充色。 */
    activeColor: z.string().catch(DEFAULT_LOADING_PAGE_PROGRESS_STYLE.activeColor),
})
    .catch({ ...DEFAULT_LOADING_PAGE_PROGRESS_STYLE });
/** 前置动画元素默认外观。 */
export const DEFAULT_INTRO_ANIMATION_ELEMENT_STYLES = {
    skip: { borderRadius: 9999, backgroundColor: 'rgba(5, 6, 8, 0.62)', backgroundAssetId: null },
};
/** 开始页元素外观表：键与 elementLayouts 一一对应。 */
export const startPageElementStylesSchema = z
    .object({
    chapterEntry: gamePageElementStyleSchema.default({
        ...DEFAULT_START_PAGE_ELEMENT_STYLES.chapterEntry,
    }),
    title: gamePageElementStyleSchema.default({ ...DEFAULT_START_PAGE_ELEMENT_STYLES.title }),
    subtitle: gamePageElementStyleSchema.default({ ...DEFAULT_START_PAGE_ELEMENT_STYLES.subtitle }),
    button: gamePageElementStyleSchema.default({ ...DEFAULT_START_PAGE_ELEMENT_STYLES.button }),
    exitButton: gamePageElementStyleSchema.default({
        ...DEFAULT_START_PAGE_ELEMENT_STYLES.exitButton,
    }),
})
    .catch({ ...DEFAULT_START_PAGE_ELEMENT_STYLES });
/** 载入页元素外观表。 */
export const loadingPageElementStylesSchema = z
    .object({
    title: gamePageElementStyleSchema.default({ ...DEFAULT_LOADING_PAGE_ELEMENT_STYLES.title }),
})
    .catch({ ...DEFAULT_LOADING_PAGE_ELEMENT_STYLES });
/** 前置动画元素外观表。 */
export const introAnimationElementStylesSchema = z
    .object({
    skip: gamePageElementStyleSchema.default({ ...DEFAULT_INTRO_ANIMATION_ELEMENT_STYLES.skip }),
})
    .catch({ ...DEFAULT_INTRO_ANIMATION_ELEMENT_STYLES });
/** 把每页的布局表映射到它们各自所属的元素标识。 */
export const GAME_PAGE_ELEMENT_LAYOUT_KEYS = {
    loading: { title: 'loadingTitle', progress: 'loadingProgress' },
    intro: { skip: 'introSkip' },
    start: {
        chapterEntry: 'startChapterEntry',
        title: 'startTitle',
        subtitle: 'startSubtitle',
        button: 'startButton',
        exitButton: 'startExitButton',
    },
};
/**
 * 画布文案的文本样式。
 *
 * 与选项文案的 TextStyle 同一套设计口径（尺寸随画布等比缩放），但字段按游戏页面配置需求裁剪：
 * 只保留字体、字号、字色、字间距与行间距。开始页标题、副标题、开始按钮，载入页标题与
 * 前置动画的跳过按钮各自持有一份，缺失时整块按该文案位的默认样式兜底。
 */
export const canvasTextStyleSchema = z.object({
    /** 字体族标识；'default' 表示跟随作品默认字体。 */
    fontFamily: z.string().catch(DEFAULT_START_BUTTON_TEXT_STYLE.fontFamily),
    /** 字号（1920 设计基准下的像素值）。 */
    fontSize: z.number().catch(DEFAULT_START_BUTTON_TEXT_STYLE.fontSize),
    /** 文字颜色（十六进制色值）。 */
    color: z.string().catch(DEFAULT_START_BUTTON_TEXT_STYLE.color),
    /** 字间距（1920 设计基准下的像素值，可为负）。 */
    letterSpacing: z.number().catch(DEFAULT_START_BUTTON_TEXT_STYLE.letterSpacing),
    /** 行间距：在字号之上额外增加的行间距（1920 设计基准下的像素值，可为负）。 */
    lineHeight: z.number().catch(DEFAULT_START_BUTTON_TEXT_STYLE.lineHeight),
});
/** 开始页标题的默认文本样式：字号与其它文案位一致，由作者按需要调大。 */
export const DEFAULT_START_TITLE_TEXT_STYLE = {
    ...DEFAULT_START_BUTTON_TEXT_STYLE,
};
/** 开始页副标题的默认文本样式：字号与其它文案位一致，颜色取正文次级色。 */
export const DEFAULT_START_SUBTITLE_TEXT_STYLE = {
    ...DEFAULT_START_BUTTON_TEXT_STYLE,
    color: '#C4C1CB',
};
/** 载入页标题的默认文本样式：比其它文案位大一号，作为整屏过渡画面的主文案。 */
export const DEFAULT_LOADING_TITLE_TEXT_STYLE = {
    ...DEFAULT_START_BUTTON_TEXT_STYLE,
    fontSize: 45,
};
/** 开始页「章节选择」入口的默认文本样式：字号与其它文案位一致。 */
export const DEFAULT_START_CHAPTER_ENTRY_TEXT_STYLE = {
    ...DEFAULT_START_BUTTON_TEXT_STYLE,
};
/** 开始页「退出」按钮的默认文本样式：字号与其它文案位一致。 */
export const DEFAULT_START_EXIT_BUTTON_TEXT_STYLE = {
    ...DEFAULT_START_BUTTON_TEXT_STYLE,
};
/** 前置动画「跳过 CG」的默认文本样式。 */
export const DEFAULT_INTRO_SKIP_TEXT_STYLE = {
    ...DEFAULT_START_BUTTON_TEXT_STYLE,
};
/**
 * 前置 CG（动画）配置。
 *
 * 素材位只接受 MP4：与播放端「前置视频用视频元素直接播放、不做解码兜底」的实现一致。
 * 未配置素材时播放端跳过本步，因此这里允许 assetId 为 null。
 */
export const introAnimationSchema = z.object({
    /** 前置 CG 素材 ID；缺失或空串表示本步未配置，播放时跳过。 */
    assetId: z.string().nullable().catch(DEFAULT_INTRO_ANIMATION.assetId),
    /** 播放完成后的动作。 */
    afterComplete: z
        .enum(INTRO_ANIMATION_AFTER_COMPLETE)
        .catch(DEFAULT_INTRO_ANIMATION.afterComplete),
    /** 是否允许跳过前置 CG。 */
    allowSkip: z.boolean().catch(DEFAULT_INTRO_ANIMATION.allowSkip),
    /** 是否仅在玩家首次进入游戏时播放。 */
    firstTimeOnly: z.boolean().catch(DEFAULT_INTRO_ANIMATION.firstTimeOnly),
    /** 「跳过 CG」按钮上的文案；历史数据缺失时按默认文案补齐。 */
    skipText: z.string().default(DEFAULT_INTRO_SKIP_TEXT),
    /** 「跳过 CG」按钮的文本样式。 */
    skipTextStyle: canvasTextStyleSchema.catch({ ...DEFAULT_INTRO_SKIP_TEXT_STYLE }),
    /** 跳过按钮在画布上的位置与尺寸。 */
    elementLayouts: introAnimationElementLayoutsSchema,
    /** 跳过按钮的外观：圆角、背景色与背景图。 */
    elementStyles: introAnimationElementStylesSchema,
});
/**
 * 把任意顺序收敛成合法的游戏页面顺序。
 *
 * 页面顺序是**可增可删的有序子集**：作者可以只放其中两页，也可以先把三页都删掉再重排。
 * 因此这里只做两件事：丢弃非法项、按首次出现去重——每种页面最多出现一次，
 * 但**不**补全缺失的步骤。空数组是合法值，表示玩家进入游戏时不经过任何配置页。
 *
 * @param order 待收敛的步骤顺序
 * @returns 去重后的合法顺序
 */
export function normalizeGamePageOrder(order) {
    const normalized = [];
    for (const step of order) {
        if (GAME_PAGE_STEP_IDS.includes(step) && !normalized.includes(step))
            normalized.push(step);
    }
    return normalized;
}
/**
 * 游戏页面顺序。
 *
 * 作者在胶片条上拖拽排序、增删页面：每种页面最多出现一次，也可以一页都不放。
 * 解析时统一走 {@link normalizeGamePageOrder} 丢弃非法项与重复项；
 * 字段整体缺失或结构损坏（不是数组、含非法枚举）时才回退成"三页齐全"的默认顺序。
 */
export const gamePageOrderSchema = z
    .array(z.enum(GAME_PAGE_STEP_IDS))
    .catch(() => [...DEFAULT_GAME_PAGE_ORDER])
    .transform((order) => normalizeGamePageOrder(order));
/**
 * 项目运行设置。
 *
 * 持久化契约只有 `loadingPage` 与 `startPage` 两个分区：`cg_project.settings_json` 只存这两项。
 * 两项都不再有启用开关与入口开关——载入页与开始页恒启用，开始页入口固定为开始游戏、选择章节、
 * 设置与退出四项，不再由设置门控；`canvasRatio` 的唯一来源是 `cg_project.screen_ratio`，
 * 由后端拼装播放文档时注入；事件交互素材已改为节点级数据，见 `ProjectNode.eventAssets`。
 *
 * `loadingPage` 与 `startPage` 都只有文案加两个素材位（背景与音效）；`startPage` 额外含
 * `subtitle` 副标题与 `buttonText` 开始按钮文案，播放端只渲染标题与副标题，
 * 项目简介属于项目管理信息，不作为开始页正文下发。
 *
 * 其余分区保留为**可选**字段，只用于兼容历史存量 settings_json：缺失时保持缺省、不被补写回文档，
 * 读取方按上面的运行默认值兜底。
 */
/**
 * 素材位：字符串、数字编号和空值都接受。
 * 创建项目时后端可能只写入 URL，历史数据也可能把素材主键存成数字；缺字段按空处理，不让整份设置解析失败。
 */
const storedAssetIdSchema = z
    .union([z.string(), z.number(), z.null()])
    .catch(null)
    .transform((value) => (typeof value === 'number' ? String(value) : value));
/**
 * 章节或节点定位。历史数据里这两个 ID 可能是数字；空串与缺失都表示未指定。
 * 最外层保持 optional，避免推断结果把这两个字段变成必填。
 */
const optionalStoredIdSchema = z
    .preprocess((value) => {
    if (value == null || value === '')
        return undefined;
    if (typeof value === 'number')
        return String(value);
    if (typeof value === 'string')
        return value;
    return undefined;
}, z.string().optional())
    .optional();
/** 已下线的布尔开关。值不是布尔时丢掉，避免旧数据挡住编辑器。 */
const legacyOptionalBooleanSchema = z.boolean().optional().catch(undefined);
/** 载入页缺省结构。分区缺失或不是对象时整段回退到这里。 */
const DEFAULT_LOADING_PAGE = {
    assetId: null,
    audioAssetId: null,
    title: DEFAULT_LOADING_PAGE_TEXTS.title,
    titleTextStyle: { ...DEFAULT_LOADING_TITLE_TEXT_STYLE },
    elementLayouts: { ...DEFAULT_LOADING_PAGE_ELEMENT_LAYOUTS },
    elementStyles: { ...DEFAULT_LOADING_PAGE_ELEMENT_STYLES },
    progressStyle: { ...DEFAULT_LOADING_PAGE_PROGRESS_STYLE },
};
/** 开始页缺省结构。分区缺失或不是对象时整段回退到这里。 */
const DEFAULT_START_PAGE = {
    assetId: null,
    audioAssetId: null,
    title: DEFAULT_START_PAGE_TEXTS.title,
    subtitle: DEFAULT_START_PAGE_TEXTS.subtitle,
    buttonText: DEFAULT_START_PAGE_TEXTS.buttonText,
    chapterEntryText: DEFAULT_START_PAGE_TEXTS.chapterEntryText,
    exitButtonText: DEFAULT_START_PAGE_TEXTS.exitButtonText,
    titleTextStyle: { ...DEFAULT_START_TITLE_TEXT_STYLE },
    subtitleTextStyle: { ...DEFAULT_START_SUBTITLE_TEXT_STYLE },
    buttonTextStyle: { ...DEFAULT_START_BUTTON_TEXT_STYLE },
    chapterEntryTextStyle: { ...DEFAULT_START_CHAPTER_ENTRY_TEXT_STYLE },
    exitButtonTextStyle: { ...DEFAULT_START_EXIT_BUTTON_TEXT_STYLE },
    elementLayouts: { ...DEFAULT_START_PAGE_ELEMENT_LAYOUTS },
    elementStyles: { ...DEFAULT_START_PAGE_ELEMENT_STYLES },
};
export const projectSettingsSchema = z.object({
    // 开始定位：缺失与空串同义，都表示从首个可播放章节的入口开始。
    startChapterId: optionalStoredIdSchema,
    startNodeId: optionalStoredIdSchema,
    // 章节选择入口恒为开启，该历史字段不再参与播放。
    showChapterSelection: legacyOptionalBooleanSchema,
    // 暂停 / 跳过 / 重播 / 回退开关：播放控制栏下线后既无编辑入口也无播放端读者，仅兼容历史数据。
    allowPause: legacyOptionalBooleanSchema,
    allowSkip: legacyOptionalBooleanSchema,
    allowRestart: legacyOptionalBooleanSchema,
    allowBacktrack: legacyOptionalBooleanSchema,
    // 画布比例：数值唯一来源是项目表 screen_ratio（cg_project.screen_ratio），
    // 编辑器画布与播放层舞台（章节预览/发布播放）共用同一换算基准，选项/QTE 的百分比布局
    // 以该比例画布为基准换算。settings_json 里这份属于待清理的历史字段：缺失时不参与换算，
    // 非法值按 16:9 兜底，因此下游读取都要容忍 undefined。
    canvasRatio: z.enum(CANVAS_RATIO_IDS).optional().catch(DEFAULT_CANVAS_RATIO_ID),
    // 载入页与开始页：只有素材与文案，恒启用。
    loadingPage: z
        .object({
        assetId: storedAssetIdSchema,
        audioAssetId: storedAssetIdSchema,
        // 标题缺失或不是字符串时用默认文案；作者保存的空串仍然保留。
        title: z.string().catch(DEFAULT_LOADING_PAGE_TEXTS.title),
        // 载入标题的文本样式：与开始页一样独立配置。
        titleTextStyle: canvasTextStyleSchema.catch({ ...DEFAULT_LOADING_TITLE_TEXT_STYLE }),
        // 载入页画布元素布局：标题与进度条。
        elementLayouts: loadingPageElementLayoutsSchema,
        // 载入页画布元素外观：目前只有标题一层（进度条外观见 progressStyle）。
        elementStyles: loadingPageElementStylesSchema,
        // 进度条外观：圆角、底轨颜色与动轨颜色。
        progressStyle: loadingPageProgressStyleSchema.catch({ ...DEFAULT_LOADING_PAGE_PROGRESS_STYLE }),
    })
        .catch(DEFAULT_LOADING_PAGE),
    startPage: z
        .object({
        assetId: storedAssetIdSchema,
        audioAssetId: storedAssetIdSchema,
        title: z.string().catch(DEFAULT_START_PAGE_TEXTS.title),
        // 副标题与开始按钮文案为增补字段：历史 settings_json 缺失或为 null 时按空串兜底，
        // 播放端对空串各自回退（副标题不渲染，按钮文案回退「开始游戏」）。
        subtitle: z.string().catch(''),
        buttonText: z.string().catch(''),
        // 章节选择入口的文案：历史数据没有这一项时按默认入口文案补；作者清空后保持为空。
        chapterEntryText: z.string().catch(DEFAULT_START_PAGE_TEXTS.chapterEntryText),
        // 退出按钮文案：与章节选择一样，历史数据缺这一项时补默认值，清空后保持为空。
        exitButtonText: z.string().catch(DEFAULT_START_PAGE_TEXTS.exitButtonText),
        // 标题、副标题与开始按钮文案各自持有一份文本样式，随开始页一起落库。
        titleTextStyle: canvasTextStyleSchema.catch({ ...DEFAULT_START_TITLE_TEXT_STYLE }),
        subtitleTextStyle: canvasTextStyleSchema.catch({ ...DEFAULT_START_SUBTITLE_TEXT_STYLE }),
        buttonTextStyle: canvasTextStyleSchema.catch({ ...DEFAULT_START_BUTTON_TEXT_STYLE }),
        chapterEntryTextStyle: canvasTextStyleSchema.catch({
            ...DEFAULT_START_CHAPTER_ENTRY_TEXT_STYLE,
        }),
        exitButtonTextStyle: canvasTextStyleSchema.catch({ ...DEFAULT_START_EXIT_BUTTON_TEXT_STYLE }),
        // 画布元素布局：章节选择入口、标题、副标题与开始按钮在画布上的位置与尺寸。
        elementLayouts: startPageElementLayoutsSchema,
        // 画布元素外观：每个元素的圆角、背景色与背景图。
        elementStyles: startPageElementStylesSchema,
    })
        .catch(DEFAULT_START_PAGE),
    // 前置 CG（动画）分区：与载入页、开始页并列，缺失时按默认配置兜底（不配素材即跳过本步）。
    introAnimation: introAnimationSchema.catch({
        ...DEFAULT_INTRO_ANIMATION,
        skipTextStyle: { ...DEFAULT_INTRO_SKIP_TEXT_STYLE },
        elementLayouts: { ...DEFAULT_INTRO_ANIMATION_ELEMENT_LAYOUTS },
        elementStyles: { ...DEFAULT_INTRO_ANIMATION_ELEMENT_STYLES },
    }),
    // 游戏页面顺序：可为空的有序子集（每种页面最多一次），缺失或损坏时回退默认顺序。
    gamePageOrder: gamePageOrderSchema,
    // 会话音量与音效：缺失时不补齐字段（保持文档精简），读取方用 DEFAULT_PROJECT_*_SETTINGS；
    // 结构损坏时同样回退默认值，避免整份文档解析失败。
    audio: z
        .object({ defaultVolume: z.number(), allowMute: z.boolean() })
        .optional()
        .catch(DEFAULT_PROJECT_AUDIO_SETTINGS),
    // position 为音乐/音效控件在播放页的界面摆放位置：百分比坐标 x（横向）/ y（纵向），0~100。
    music: z
        .object({
        enabled: z.boolean(),
        volume: z.number(),
        position: z.object({ x: z.number(), y: z.number() }).catch({ x: 0, y: 0 }),
    })
        .optional()
        .catch(undefined),
    sfx: z
        .object({
        enabled: z.boolean(),
        volume: z.number(),
        position: z.object({ x: z.number(), y: z.number() }).catch({ x: 0, y: 0 }),
    })
        .optional()
        .catch(DEFAULT_PROJECT_SFX_SETTINGS),
    // 背景图与滚动条底图/拖动图曾用于设置页预览，设置页签下线后仅兼容历史数据。
    system: z
        .object({
        bgAssetId: storedAssetIdSchema,
        scrollBgAssetId: storedAssetIdSchema,
        scrollThumbAssetId: storedAssetIdSchema,
    })
        .optional()
        .catch(undefined),
    // 事件交互素材的历史项目级分区：唯一编辑入口已改为节点级 eventAssets。
    // 结构不完整时丢掉这一段，编辑器改用节点上的素材，不能因此打不开节点。
    eventAssets: eventAssetsSchema.optional().catch(undefined),
});
/**
 * 项目还没有设置，或设置分区缺失时使用的运行设置。
 * 编辑器不能因为 settings_json 为空就停在准备页。
 * @returns 带载入页、开始页和默认页面顺序的设置
 */
export function createDefaultProjectSettings() {
    return projectSettingsSchema.parse({});
}
/**
 * 把设置分区的音频配置补全为运行时必填形态。
 *
 * settings_json 不再持久化 audio/sfx，播放与编辑器一律经这里取音频配置，
 * 缺失时使用运行默认值，保证存量项目与新项目表现一致。
 *
 * @param settings 项目运行设置
 * @returns 音频与音效配置，字段均非空
 */
export function resolveProjectAudioSettings(settings) {
    return {
        audio: settings.audio ?? DEFAULT_PROJECT_AUDIO_SETTINGS,
        sfx: settings.sfx ?? DEFAULT_PROJECT_SFX_SETTINGS,
    };
}
/** 项目文档完整结构，与 docs/CinematicGaming-ProjectDocument.md 对齐。 */
export const projectDocumentSchema = z
    .object({
    schemaVersion: z.string(),
    revision: z.number().int(),
    documentType: z.literal('cinematic-gaming-project'),
    project: z.object({
        id: z.string(),
        name: z.string(),
        description: z.string(),
        coverAssetId: z.string().nullable(),
        status: z.enum(['draft', 'published']),
        createdBy: z.string(),
        createdAt: z.string(),
        updatedAt: z.string(),
        /**
         * 项目数值定义（名称 + 初始值）：后端从 cg_project.game_variables 拼装下发。
         * 播放端用它初始化变量表，再按选项 / QTE 结果的 variableEffects 逐次运算；
         * 历史文档缺失该字段时兜底为空数组，不影响其余分区解析。
         */
        gameVariables: z.array(gameVariableSchema).default([]),
    }),
    settings: projectSettingsSchema,
    chapters: z.array(projectChapterSchema),
    // release 分区只保存与 cg_project_version 表的轻量关联：当前生效版本（最新、非历史）的记录 ID。
    // 版本号、名称、状态、封面、标签等全量信息以版本表为准，前端按需调版本接口获取；
    // 历史版本只存版本表，不进文档。老文档的多余镜像字段由 Zod 剥离，缺失时兜底 null。
    release: z.object({
        versionId: z.number().int().nullable().catch(null),
    }),
    collaboration: z.object({
        updatedBy: z.string(),
        lastOperationId: z.string(),
        activeLocks: z.array(extensibleObjectSchema),
    }),
})
    .superRefine(validateProjectDocument);
/**
 * 清除选项文案中的冗余系统前缀（如“图文选项 · ”、“文字选项_”等），保证文案纯净。
 */
export function cleanOptionLabel(rawLabel) {
    if (!rawLabel)
        return rawLabel;
    const cleaned = rawLabel.replace(/^(?:图文选项|文字选项)\s*[·_]\s*/, '').trim();
    return cleaned || rawLabel;
}
/**
 * 修复与自愈项目文档中的非破坏性数据异常（如 choice 分支缺失 eventId/optionId、结局节点有残留分支、孤立边等）。
 * 保证历史存量文档或外部异常数据能够平滑加载，并在下次保存时自动纠正落库。
 * 备注：本函数后续宜重构为基于 Zod partial schema 的类型安全实现，以消除对 any 宽松断言的依赖。
 */
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument, @typescript-eslint/prefer-optional-chain, @typescript-eslint/prefer-nullish-coalescing, @typescript-eslint/restrict-template-expressions, prefer-const -- 自愈逻辑面向不可信的历史/外部数据，字段形状无 schema 保证，先以 any 宽松处理；重构后可移除本注释。 */
export function sanitizeProjectDocument(raw) {
    if (!raw || typeof raw !== 'object') {
        return raw;
    }
    const doc = raw;
    if (!Array.isArray(doc.chapters)) {
        return raw;
    }
    return {
        ...doc,
        chapters: doc.chapters.map((chapter) => {
            if (!chapter || typeof chapter !== 'object' || !Array.isArray(chapter.nodes)) {
                return chapter;
            }
            const allValidBranchIds = new Set();
            const nodeIdsSet = new Set();
            const nextNodes = chapter.nodes.map((node) => {
                if (!node || typeof node !== 'object')
                    return node;
                nodeIdsSet.add(node.id);
                // 1. 结局节点：强制清空分支与事件
                if (node.type === 'ending') {
                    return {
                        ...node,
                        branches: [],
                        events: [],
                    };
                }
                const events = Array.isArray(node.events) ? [...node.events] : [];
                const branches = Array.isArray(node.branches) ? [...node.branches] : [];
                // 2. 检查 choice 分支并自愈对齐事件（按 eventId 分组，避免多事件选项串合与污染）
                const choiceBranches = branches.filter((b) => b && b.type === 'choice');
                if (choiceBranches.length > 0) {
                    const choiceEvents = events.filter((e) => e && e.type === 'choice');
                    const allBranchMap = new Map(branches.map((b) => [b.id, b]));
                    // 按 eventId 分组 choiceBranches
                    const branchesByEventId = new Map();
                    for (const b of choiceBranches) {
                        let eventId = b.eventId;
                        if (!eventId) {
                            const matchedEvent = choiceEvents.find((evt) => Array.isArray(evt.options) &&
                                evt.options.some((opt) => opt.branchId === b.id || opt.id === b.optionId));
                            if (matchedEvent) {
                                eventId = matchedEvent.id;
                            }
                            else if (choiceEvents.length === 1) {
                                eventId = choiceEvents[0].id;
                            }
                            else {
                                eventId = `event-choice-${node.id}`;
                            }
                        }
                        const list = branchesByEventId.get(eventId) ?? [];
                        list.push(b);
                        branchesByEventId.set(eventId, list);
                    }
                    const nextChoiceEvents = [];
                    const repairedChoiceBranches = [];
                    const processedEventIds = new Set();
                    for (const existingChoice of choiceEvents) {
                        processedEventIds.add(existingChoice.id);
                        const groupBranches = branchesByEventId.get(existingChoice.id) ?? [];
                        // 过滤掉原属于其他 choiceEvent 的残留/污染选项
                        const rawOptions = Array.isArray(existingChoice.options)
                            ? [...existingChoice.options]
                            : [];
                        const filteredOptions = rawOptions.filter((opt) => {
                            if (!opt.branchId)
                                return true;
                            const b = allBranchMap.get(opt.branchId);
                            if (b && b.eventId && b.eventId !== existingChoice.id) {
                                return false;
                            }
                            return true;
                        });
                        const optionLayouts = { ...(existingChoice.optionLayouts ?? {}) };
                        for (let i = 0; i < groupBranches.length; i++) {
                            const b = groupBranches[i];
                            let optionId = b.optionId;
                            if (!optionId) {
                                const matchedOpt = filteredOptions.find((o) => o.branchId === b.id);
                                optionId = matchedOpt ? matchedOpt.id : `opt-${b.id}`;
                            }
                            const cleanLabel = cleanOptionLabel(b.label || `选项 ${i + 1}`);
                            let optIdx = filteredOptions.findIndex((o) => o.id === optionId);
                            if (optIdx < 0) {
                                const newOpt = {
                                    id: optionId,
                                    label: cleanLabel,
                                    order: filteredOptions.length + 1,
                                    branchId: b.id,
                                };
                                filteredOptions.push(newOpt);
                                if (!optionLayouts[optionId]) {
                                    optionLayouts[optionId] = {
                                        x: 20,
                                        y: 30 + filteredOptions.length * 15,
                                        width: 60,
                                        height: 10,
                                    };
                                }
                            }
                            else {
                                filteredOptions[optIdx] = {
                                    ...filteredOptions[optIdx],
                                    branchId: b.id,
                                    label: cleanLabel,
                                };
                            }
                            repairedChoiceBranches.push({
                                ...b,
                                label: cleanLabel,
                                eventId: existingChoice.id,
                                optionId,
                                outcomeId: null,
                            });
                        }
                        const sanitizedOptions = filteredOptions.map((opt) => ({
                            ...opt,
                            label: cleanOptionLabel(opt.label),
                        }));
                        const validOptionIds = new Set(sanitizedOptions.map((o) => o.id));
                        const sanitizedOptionLayouts = Object.fromEntries(Object.entries(optionLayouts).filter(([id]) => validOptionIds.has(id)));
                        nextChoiceEvents.push({
                            ...existingChoice,
                            options: sanitizedOptions,
                            optionLayouts: sanitizedOptionLayouts,
                        });
                    }
                    // 处理有 choiceBranches 但未在 existingChoiceEvents 中出现的全新 eventId
                    branchesByEventId.forEach((groupBranches, eventId) => {
                        if (processedEventIds.has(eventId))
                            return;
                        const newOptions = [];
                        const optionLayouts = {};
                        for (let i = 0; i < groupBranches.length; i++) {
                            const b = groupBranches[i];
                            const optionId = b.optionId || `opt-${b.id}`;
                            const cleanLabel = cleanOptionLabel(b.label || `选项 ${i + 1}`);
                            newOptions.push({
                                id: optionId,
                                label: cleanLabel,
                                order: i + 1,
                                branchId: b.id,
                                variableEffects: [],
                            });
                            optionLayouts[optionId] = {
                                x: 20,
                                y: 30 + (i + 1) * 15,
                                width: 60,
                                height: 10,
                            };
                            repairedChoiceBranches.push({
                                ...b,
                                label: cleanLabel,
                                eventId,
                                optionId,
                                outcomeId: null,
                            });
                        }
                        nextChoiceEvents.push({
                            id: eventId,
                            type: 'choice',
                            title: '选项交互',
                            anchor: { clipId: null, offsetMs: 0, mode: 'sequence-absolute' },
                            config: {
                                variant: 'text',
                                backgroundAssetId: null,
                                description: '',
                                pauseVideo: true,
                                timeoutMs: null,
                                timeoutAction: DEFAULT_INTERACTION_TIMEOUT_ACTION,
                                timeoutJumpTargetNodeId: null,
                                frameLoop: null,
                                descriptionLayout: null,
                                countdownLayout: null,
                            },
                            options: newOptions,
                            optionLayouts,
                        });
                    });
                    // 保留非 choice 的其他事件，合并 choice 事件
                    const nonChoiceEvents = events.filter((e) => e && e.type !== 'choice');
                    const finalEvents = [...nonChoiceEvents, ...nextChoiceEvents];
                    // 处理 auto 分支规范并组合所有分支
                    const sanitizedOtherBranches = branches
                        .filter((b) => b && b.type !== 'choice')
                        .map((b) => {
                        if (b && b.type === 'auto') {
                            return {
                                ...b,
                                eventId: null,
                                optionId: null,
                                outcomeId: null,
                            };
                        }
                        return b;
                    });
                    const allNodeBranches = [...sanitizedOtherBranches, ...repairedChoiceBranches];
                    allNodeBranches.forEach((b) => allValidBranchIds.add(b.id));
                    return {
                        ...node,
                        events: finalEvents,
                        branches: allNodeBranches,
                    };
                }
                const sanitizedEvents = events.map((e) => {
                    if (e && e.type === 'choice' && Array.isArray(e.options)) {
                        return {
                            ...e,
                            options: e.options.map((opt) => ({
                                ...opt,
                                label: cleanOptionLabel(opt.label),
                            })),
                        };
                    }
                    return e;
                });
                // 3. 处理 auto 分支规范
                const sanitizedBranches = branches.map((b) => {
                    if (b && b.type === 'auto') {
                        return {
                            ...b,
                            eventId: null,
                            optionId: null,
                            outcomeId: null,
                        };
                    }
                    return b;
                });
                sanitizedBranches.forEach((b) => allValidBranchIds.add(b.id));
                return {
                    ...node,
                    events: sanitizedEvents,
                    branches: sanitizedBranches,
                };
            });
            // 4. 清理无效边
            const nextEdges = Array.isArray(chapter.edges)
                ? chapter.edges.filter((e) => e && allValidBranchIds.has(e.branchId) && nodeIdsSet.has(e.targetNodeId))
                : [];
            return {
                ...chapter,
                nodes: nextNodes,
                edges: nextEdges,
            };
        }),
    };
}
/**
 * 解析项目文档：所有从外部（后端、本地草稿、导入）进入工作区的原始文档
 * 都通过此入口完成 schema 校验。前置执行自愈修补，确保历史数据平滑可用。
 * 校验失败时逐条打印「字段路径 + 原因」，便于定位是协议哪一项不符。
 */
export function parseProjectDocument(raw) {
    try {
        return projectDocumentSchema.parse(sanitizeProjectDocument(raw));
    }
    catch (error) {
        if (error instanceof ZodError) {
            const detail = error.issues
                .map((issue) => `${issue.path.join('.') || '<root>'}: ${issue.message}`)
                .join('\n');
            console.error(`[parseProjectDocument] 项目文档校验失败：\n${detail}`);
        }
        else {
            console.error('[parseProjectDocument] 项目文档解析异常：', error);
        }
        throw error;
    }
}
/** 创建节点默认时间轴：固定四类系统轨道，全部为空。 */
export function createDefaultTimeline() {
    return {
        id: createId(),
        durationMs: 0,
        tracks: [
            {
                id: createId(),
                type: 'frame-loop-marker',
                name: '帧循环标识轨',
                items: [],
                isHidden: false,
                isLocked: false,
            },
            {
                id: createId(),
                type: 'video',
                name: '视频轨道',
                items: [],
                isHidden: false,
                isLocked: false,
            },
            {
                id: createId(),
                type: 'interaction',
                name: '互动交互轨道',
                items: [],
                isHidden: false,
                isLocked: false,
            },
            {
                id: createId(),
                type: 'qte',
                name: 'QTE 标识轨道',
                items: [],
                isHidden: false,
                isLocked: false,
            },
        ],
    };
}
/** 返回一个可直接随 /project/create 提交的空项目文档。 */
export function createEmptyProjectDocument(seed) {
    const now = new Date().toISOString();
    const document = {
        schemaVersion: '1.0.0',
        revision: 0,
        documentType: 'cinematic-gaming-project',
        project: {
            id: '',
            name: seed.name,
            description: seed.description ?? '',
            coverAssetId: null,
            status: 'draft',
            createdBy: '',
            createdAt: now,
            updatedAt: now,
            gameVariables: [],
        },
        /**
         * 设置分区只写 loadingPage 与 startPage：这两项是作者可配置且要随项目持久化的内容。
         * 两项都不带启用开关；事件交互素材属于节点级数据（后端在生成节点时写入），
         * 会话音量与音效由读取方按 DEFAULT_PROJECT_* 兜底，见 projectSettingsSchema 说明。
         */
        settings: {
            loadingPage: {
                assetId: null,
                audioAssetId: null,
                // 新建项目直接带上默认文案：编辑器画布与播放端都要有可读的初始内容。
                title: DEFAULT_LOADING_PAGE_TEXTS.title,
                titleTextStyle: { ...DEFAULT_LOADING_TITLE_TEXT_STYLE },
                elementLayouts: { ...DEFAULT_LOADING_PAGE_ELEMENT_LAYOUTS },
                elementStyles: { ...DEFAULT_LOADING_PAGE_ELEMENT_STYLES },
                progressStyle: { ...DEFAULT_LOADING_PAGE_PROGRESS_STYLE },
            },
            startPage: {
                assetId: null,
                audioAssetId: null,
                title: DEFAULT_START_PAGE_TEXTS.title,
                subtitle: DEFAULT_START_PAGE_TEXTS.subtitle,
                buttonText: DEFAULT_START_PAGE_TEXTS.buttonText,
                chapterEntryText: DEFAULT_START_PAGE_TEXTS.chapterEntryText,
                exitButtonText: DEFAULT_START_PAGE_TEXTS.exitButtonText,
                titleTextStyle: { ...DEFAULT_START_TITLE_TEXT_STYLE },
                subtitleTextStyle: { ...DEFAULT_START_SUBTITLE_TEXT_STYLE },
                buttonTextStyle: { ...DEFAULT_START_BUTTON_TEXT_STYLE },
                chapterEntryTextStyle: { ...DEFAULT_START_CHAPTER_ENTRY_TEXT_STYLE },
                exitButtonTextStyle: { ...DEFAULT_START_EXIT_BUTTON_TEXT_STYLE },
                elementLayouts: { ...DEFAULT_START_PAGE_ELEMENT_LAYOUTS },
                elementStyles: { ...DEFAULT_START_PAGE_ELEMENT_STYLES },
            },
            introAnimation: {
                ...DEFAULT_INTRO_ANIMATION,
                skipTextStyle: { ...DEFAULT_INTRO_SKIP_TEXT_STYLE },
                elementLayouts: { ...DEFAULT_INTRO_ANIMATION_ELEMENT_LAYOUTS },
                elementStyles: { ...DEFAULT_INTRO_ANIMATION_ELEMENT_STYLES },
            },
            gamePageOrder: [...DEFAULT_GAME_PAGE_ORDER],
        },
        chapters: [],
        release: {
            versionId: null,
        },
        collaboration: { updatedBy: '', lastOperationId: '', activeLocks: [] },
    };
    return projectDocumentSchema.parse(document);
}
